import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, FileText, BookOpen, Building2 } from 'lucide-react';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  // Redirect if already logged in
  if (isAuthenticated) {
    navigate('/admin/dashboard');
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(formData.email, formData.password);
      toast.success(`Welcome back, ${user.full_name}!`);
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 justify-center mb-4">
            <img src="/logo-icon.svg" alt="Tinkle Tent" className="h-16 w-16" />
            <div className="text-left flex flex-col">
              <span className="font-bold text-[#194461] text-2xl leading-none">Tinkle Tent</span>
              <span className="text-sm text-slate-500 leading-none -mt-0.5">Rentals</span>
            </div>
          </Link>
          <h1 className="text-2xl font-bold text-slate-900">Operations Console</h1>
          <p className="text-slate-600 mt-1">Sign in to manage operations</p>
        </div>

        <Card className="border-slate-200">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl" data-testid="login-title">Sign In</CardTitle>
            <CardDescription>
              Enter your credentials to access the admin console
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="admin@tinkletent.com"
                  autoComplete="email"
                  data-testid="input-email"
                />
              </div>

              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="••••••••"
                    autoComplete="current-password"
                    data-testid="input-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#194461] hover:bg-[#091D2E]"
                disabled={loading}
                data-testid="login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            {/* Demo credentials hint */}
            <div className="mt-6 p-4 bg-slate-50 rounded-lg">
              <p className="text-xs font-medium text-slate-600 mb-2">Demo Credentials:</p>
              <div className="space-y-1 text-xs text-slate-500">
                <p><span className="font-medium">Admin:</span> admin@tinkletent.com / admin123</p>
                <p><span className="font-medium">Dispatcher:</span> dispatch@tinkletent.com / dispatch123</p>
                <p><span className="font-medium">Driver:</span> mike@tinkletent.com / driver123</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* New Client Setup CTA */}
        <div className="mt-6 p-4 bg-gradient-to-r from-[#194461]/10 to-[#52B8E1]/10 rounded-lg border border-[#194461]/20">
          <div className="flex items-start gap-3">
            <Building2 size={20} className="text-[#194461] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-slate-800 mb-1">
                Are you a portable toilet company wanting your own system?
              </p>
              <Link 
                to="/admin/new-client-setup" 
                className="text-sm text-[#194461] hover:text-[#52B8E1] font-medium hover:underline"
                data-testid="link-new-client-setup"
              >
                Fill out our New Client Setup form →
              </Link>
            </div>
          </div>
        </div>

        {/* Resources Links */}
        <div className="mt-6 p-4 bg-[#194461]/5 rounded-lg border border-[#194461]/10">
          <p className="text-xs font-medium text-[#194461] mb-3">Resources:</p>
          <div className="flex flex-wrap gap-3">
            <a 
              href="/sales-sheet.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-[#194461]"
              data-testid="link-sales-sheet"
            >
              <FileText size={14} />
              Sales Sheet
            </a>
            <a 
              href="/demo-guide.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-[#194461]"
              data-testid="link-demo-guide"
            >
              <BookOpen size={14} />
              Demo Guide
            </a>
          </div>
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          <Link to="/" className="hover:text-[#194461]">← Back to website</Link>
        </p>
      </div>
    </div>
  );
}
